import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import { useEffect, useMemo, useState } from "react";
import { useAppDispatch, useAppSelector } from "@/stores";
import { DateRange } from "@/domains/common";
import { DashboardChartDateRangeData } from "@/domains/dashboard";
import { tenantDashboardSelects, tenantDashboardThunks } from "./store";
// components
import { Spin } from "antd";
import { DateRangeButtonGroup, TransferBarChart } from "@/components";

const formatChartData = (arrayData: DashboardChartDateRangeData[]) => {
  return arrayData.map((rawData) => {
    const datetime = dayjs(rawData.label);
    const hour = datetime.hour().toString().padStart(2, "0");
    return {
      name: datetime.format("MM/DD HH:mm"),
      count: rawData.value,
      hour,
    };
  });
};

export const TenantHomePage = () => {
  const { t } = useTranslation();

  const dispatch = useAppDispatch();

  const loading = useAppSelector(tenantDashboardSelects.loading);

  const dashboardData = useAppSelector(tenantDashboardSelects.data);

  useEffect(() => {
    void dispatch(tenantDashboardThunks.read());
  }, [dispatch]);

  const [dateRangeForTotal, setDateRangeForTotal] = useState<DateRange>(
    DateRange.day0
  );

  const [dateRangeForDeposit, setDateRangeForDeposit] = useState<DateRange>(
    DateRange.day0
  );

  const totalData = useMemo(() => {
    if (!dashboardData) return;
    return dashboardData.summary.total[dateRangeForTotal];
  }, [dashboardData, dateRangeForTotal]);

  const depositChartCounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.deposit_in_24_hours.counts);
  }, [dashboardData]);

  const depositChartAmounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.deposit_in_24_hours.amounts);
  }, [dashboardData]);

  const depositData = useMemo(() => {
    if (!dashboardData) return;
    return dashboardData.summary.deposit[dateRangeForDeposit];
  }, [dashboardData, dateRangeForDeposit]);

  const withdrawChartCounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.withdraw_in_24_hours.counts);
  }, [dashboardData]);

  const withdrawChartAmounts = useMemo(() => {
    if (!dashboardData) return [];
    return formatChartData(dashboardData.chart.withdraw_in_24_hours.amounts);
  }, [dashboardData]);

  return (
    <div className="flex flex:col gap:16">
      <Spin spinning={loading}>
        <div className="py:20 px:16 flex ai:center gap:8 bg:white r:8">
          <div className="flex:1">
            <div className="f:18 f:bold fg:gray-48 t:center t:underline">
              {t("pages.Home.operationOverview")}
            </div>

            <DateRangeButtonGroup
              dateRange={dateRangeForTotal}
              onChange={setDateRangeForTotal}
            />

            <div className="w:full mt:24 flex gap:16">
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.depositUnit")}：
                </div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {Number.isInteger(totalData?.deposit)
                    ? totalData?.deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.tenantDepositUnit")}：
                </div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(totalData?.tenant_deposit)
                    ? totalData?.tenant_deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.tenant_deposit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex:1" />
            </div>
            <div className="w:full mt:24 flex gap:16">
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.withdrawUnit")}：
                </div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {Number.isInteger(totalData?.withdraw)
                    ? totalData?.withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.tenantWithdrawUnit")}：
                </div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(totalData?.tenant_withdraw)
                    ? totalData?.tenant_withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.tenant_withdraw.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.totalServiceFee")}：
                </div>
                <div className="flex:1 f:bold fg:warning t:right">
                  {Number.isInteger(totalData?.profit)
                    ? totalData?.profit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : totalData?.profit.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
            </div>
          </div>

          <div className="h:240 mr:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48 t:underline">
              {t("pages.Home.countOfDepositIn24Hours")}
            </div>

            <TransferBarChart
              baseline={5}
              data={depositChartCounts}
              yAxisLabel={{ value: t("pages.Home.transferCount") }}
            />
          </div>

          <div className="h:240 ml:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48 t:underline">
              {t("pages.Home.unitOfDepositIn24Hours")}
            </div>

            <TransferBarChart
              baseline={5000}
              data={depositChartAmounts}
              yAxisLabel={{ value: t("pages.Home.transferUnit") }}
            />
          </div>
        </div>
      </Spin>

      <Spin spinning={loading}>
        <div className="py:20 px:16 flex ai:center gap:8 bg:white r:8">
          <div className="flex:1">
            <div className="f:18 f:bold fg:gray-48 t:center t:underline">
              {t("pages.Home.depositSummary")}
            </div>

            <DateRangeButtonGroup
              dateRange={dateRangeForDeposit}
              onChange={setDateRangeForDeposit}
            />

            <div className="w:full mt:24 flex gap:16">
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.countOfCreatedDeposit")}：
                </div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {depositData?.created.count.toLocaleString()}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.unitOfCreatedDeposit")}：
                </div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(depositData?.created.amount)
                    ? depositData?.created.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : depositData?.created.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex:1" />
            </div>

            <div className="w:full mt:24 flex gap:16">
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.countOfArrivedDeposit")}：
                </div>
                <div className="flex:1 f:bold fg:primary t:right">
                  {depositData?.arrived.count.toLocaleString()}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.unitOfArrivedDeposit")}：
                </div>
                <div className="flex:1 f:bold fg:success t:right">
                  {Number.isInteger(depositData?.arrived.amount)
                    ? depositData?.arrived.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })
                    : depositData?.arrived.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0,
                      })}
                </div>
              </div>
              <div className="flex flex:1">
                <div className="w:80 white-space:nowrap">
                  {t("pages.Home.depositRate")}：
                </div>
                <div className="flex:1 f:bold fg:warning t:right">
                  {Number.isInteger(depositData?.rate)
                    ? depositData?.rate
                    : depositData?.rate.toFixed(2)}{" "}
                  %
                </div>
              </div>
            </div>
          </div>

          <div className="h:240 mr:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48 t:underline">
              {t("pages.Home.countOfWithdrawIn24Hours")}
            </div>

            <TransferBarChart
              baseline={5}
              data={withdrawChartCounts}
              yAxisLabel={{ value: t("pages.Home.transferCount") }}
            />
          </div>

          <div className="h:240 ml:-16 flex:1">
            <div className="mb:16 t:center f:bold fg:gray-48 t:underline">
              {t("pages.Home.unitOfWithdrawIn24Hours")}
            </div>

            <TransferBarChart
              baseline={5000}
              data={withdrawChartAmounts}
              yAxisLabel={{ value: t("pages.Home.transferUnit") }}
            />
          </div>
        </div>
      </Spin>
    </div>
  );
};
